import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Pencil, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import { buildRegionOptions, buildCommuneOptions } from '../../../../../../features/geo/geoUtils';
import { COUNTRY_ISO2 } from '../../../../../../features/geo/geoConstants';
import {
  PLAN_OPTIONS,
  WAREHOUSE_SHIPPING_METHOD_OPTIONS,
} from '../../../../../../features/sellers/sellerFormConstants';
import { isValid } from '../../../../../../features/sellers/sellerFormUtils';
import { composePhone } from '../../../../../../utils/phone';

const labelOf = (options, value) =>
  options.find((option) => String(option.value) === String(value))?.label || value || '-';

const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5">
    <span className="text-sm text-text-secondary shrink-0">{label}</span>
    <span className="text-sm font-medium text-ninja-dark dark:text-white text-right break-words min-w-0">
      {value || '-'}
    </span>
  </div>
);

const Block = ({ title, hasErrors, onEdit, children }) => (
  <section
    className={clsx(
      'rounded-2xl border bg-white dark:bg-[#1a1a1a] overflow-hidden',
      hasErrors ? 'border-danger' : 'border-border'
    )}
  >
    <header className="flex items-center justify-between gap-4 px-5 py-3 border-b border-border bg-ninja-gray/40 dark:bg-[#111]">
      <h3 className="font-bold text-ninja-dark dark:text-white flex items-center gap-2">
        {title}
        {hasErrors && <AlertCircle size={15} className="text-danger" />}
      </h3>
      <button
        type="button"
        onClick={onEdit}
        className="flex items-center gap-1.5 text-sm font-semibold text-ninja-green hover:underline"
      >
        <Pencil size={14} />
        Editar
      </button>
    </header>
    <div className="px-5 py-3 divide-y divide-border">{children}</div>
  </section>
);

/**
 * Everything about to be created, in one screen.
 *
 * Worth a step of its own because the request is not incremental: one call
 * opens a user account, a company, two addresses and every warehouse, and
 * mails the seller its password. Reviewing beats undoing, since there is no
 * undo.
 *
 * Place names are resolved back to their catalog labels — the form holds the
 * system names the backend wants, which are unreadable as a summary.
 *
 * @param {object} props
 * @param {object} props.form
 * @param {string} props.country
 * @param {Array<{value: number, label: string}>} props.zoneOptions
 * @param {Object.<string, object>} props.stepErrors - Errors by step id
 * @param {(stepId: string) => void} props.onEditStep
 */
export const SummaryStep = ({ form, country, zoneOptions, stepErrors, onEditStep }) => {
  const { regionsByCountry } = useSelector((state) => state.geo);
  const regions = useMemo(() => regionsByCountry[country] || [], [regionsByCountry, country]);
  const isMexico = country === COUNTRY_ISO2.MX;

  const regionOptions = useMemo(() => buildRegionOptions(regions), [regions]);

  const describePlace = (source) => {
    const communeOptions = buildCommuneOptions(regions, source.region);
    const commune = labelOf(communeOptions, source.commune);
    const region = labelOf(regionOptions, source.region);
    return [commune, region].filter(Boolean).join(', ');
  };

  const describeAddress = (source) => {
    const line = [source.street, source.streetNumber].filter(Boolean).join(' ');
    const colonia = isMexico && source.settlement ? source.settlement : '';
    return [line, colonia, describePlace(source)].filter(Boolean).join(' · ');
  };

  return (
    <div className="space-y-5">
      <Block
        title="Empresa"
        hasErrors={!isValid(stepErrors.company)}
        onEdit={() => onEditStep('company')}
      >
        <Row label="Razón social" value={form.razonSocial} />
        <Row label={isMexico ? 'RFC' : 'RUT'} value={form.taxId} />
        <Row label="Plan" value={labelOf(PLAN_OPTIONS, form.plan)} />
      </Block>

      <Block
        title="Cuenta"
        hasErrors={!isValid(stepErrors.account)}
        onEdit={() => onEditStep('account')}
      >
        <Row label="Correo" value={form.email} />
      </Block>

      <Block
        title="Dirección comercial"
        hasErrors={!isValid(stepErrors.address)}
        onEdit={() => onEditStep('address')}
      >
        <Row label="Dirección" value={describeAddress(form)} />
        {isMexico && <Row label="Código postal" value={form.postalCode} />}
      </Block>

      <Block
        title={`Bodegas (${form.warehouses.length})`}
        hasErrors={!isValid(stepErrors.warehouses)}
        onEdit={() => onEditStep('warehouses')}
      >
        {form.warehouses.map((warehouse, index) => (
          <div key={warehouse.id} className="py-3 first:pt-1.5 last:pb-1.5">
            <p className="font-semibold text-sm text-ninja-dark dark:text-white">
              {warehouse.name || `Bodega ${index + 1}`}
              {index === 0 && (
                <span className="ml-2 text-xs font-medium text-ninja-green">Por defecto</span>
              )}
            </p>
            <Row label="Dirección" value={describeAddress(warehouse)} />
            <Row label="Encargado" value={warehouse.storeKeeper} />
            <Row
              label="WhatsApp"
              value={composePhone(warehouse.customerPhoneCountry, warehouse.customerPhone)}
            />
            <Row label="Zona" value={labelOf(zoneOptions, warehouse.shippingZone)} />
            <Row
              label="Método"
              value={labelOf(WAREHOUSE_SHIPPING_METHOD_OPTIONS, warehouse.shippingMethod)}
            />
          </div>
        ))}
      </Block>
    </div>
  );
};
